import React from 'react';
import { Link } from 'react-router-dom';
import { Github, Linkedin, Mail, Zap } from 'lucide-react';
import { useWindowWidth } from '../hooks/useWindowWidth';
import Logo from '../assets/Logo.png';

const Footer: React.FC = () => {
  const isMobile = useWindowWidth() < 768;

  return (
    <footer style={{
      background: 'var(--bg-surface)',
      borderTop: '1px solid var(--border-card)',
      padding: isMobile ? '48px 24px 28px' : '64px var(--page-padding-x) 32px',
    }}>
      <div style={{ maxWidth: 'var(--page-max-width)', margin: '0 auto' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr 1fr',
          gap: isMobile ? '36px' : '48px',
          marginBottom: isMobile ? '40px' : '56px',
        }}>
          {/* Brand */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <img src={Logo} alt="Elola Technologies" style={{ height: '36px', width: 'auto' }} />
              <span style={{
                fontWeight: 700,
                fontSize: '15px',
                background: 'linear-gradient(135deg, #fff 0%, var(--accent-purple-light) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                Elola Technologies
              </span>
            </div>
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.7', maxWidth: '280px' }}>
              Turning ideas into digital gold. We build cutting-edge web, AI, and blockchain solutions for ambitious brands.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              {[
                { href: 'https://www.linkedin.com/in/marcus-elola-b531b7133/', icon: <Linkedin size={17} />, label: 'LinkedIn' },
                { href: 'https://github.com/elolamarcus100', icon: <Github size={17} />, label: 'GitHub' },
                { href: '/contact', icon: <Mail size={17} />, label: 'Contact', isInternal: true },
              ].map(({ href, icon, label, isInternal }) => (
                isInternal ? (
                  <Link
                    key={label}
                    to={href}
                    aria-label={label}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '38px',
                      height: '38px',
                      borderRadius: '10px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid var(--border-card)',
                      color: 'var(--text-secondary)',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.color = 'var(--accent-purple-light)';
                      (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-subtle)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.color = 'var(--text-secondary)';
                      (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-card)';
                    }}
                  >
                    {icon}
                  </Link>
                ) : (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '38px',
                      height: '38px',
                      borderRadius: '10px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid var(--border-card)',
                      color: 'var(--text-secondary)',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.color = 'var(--accent-purple-light)';
                      (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-subtle)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.color = 'var(--text-secondary)';
                      (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-card)';
                    }}
                  >
                    {icon}
                  </a>
                )
              ))}
            </div>
          </div>

          {/* Pages */}
          <FooterCol title="Pages" links={[
            { label: 'Home', to: '/' },
            { label: 'Services', to: '/services' },
            { label: 'Projects', to: '/projects' },
            { label: 'Contact', to: '/contact' },
          ]} />

          {/* Services */}
          <FooterCol title="Services" links={[
            { label: 'Custom AI', to: '/services' },
            { label: 'Web Development', to: '/services' },
            { label: 'E-Commerce', to: '/services' },
            { label: 'Blockchain & Web3', to: '/services' },
            { label: 'App Creation', to: '/services' },
          ]} />

          {/* Tools */}
          <FooterCol title="Tools" links={[
            { label: 'AI Chat', to: '/chat' },
            { label: 'Our GPTs', to: '/GPTs' },
          ]} />
        </div>

        {/* Bottom bar */}
        <div style={{
          paddingTop: '24px',
          borderTop: '1px solid var(--border-card)',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'center' : 'center',
          justifyContent: 'space-between',
          gap: '12px',
        }}>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
            © {new Date().getFullYear()} Elola Technologies. All rights reserved.
          </p>
          <Link
            to="/contact"
            className="btn btn-primary"
            style={{ padding: '9px 20px', fontSize: '13px' }}
          >
            <Zap size={13} />
            Start a Project
          </Link>
        </div>
      </div>
    </footer>
  );
};

const FooterCol: React.FC<{ title: string; links: { label: string; to: string }[] }> = ({ title, links }) => (
  <div>
    <h4 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '16px' }}>
      {title}
    </h4>
    <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {links.map(({ label, to }) => (
        <li key={label}>
          <Link
            to={to}
            style={{ fontSize: '14px', color: 'var(--text-secondary)', transition: 'color 0.2s' }}
            onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.color = '#fff'}
            onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.color = 'var(--text-secondary)'}
          >
            {label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

export default Footer;
